import { FileActionButton } from "../file-action/file-action-controls";
import React, { useState, useRef, Suspense, useEffect } from 'react';
import { DataTable, DataTableExpandedRows } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import AppLoading from '../app-loading/app-loading';
import api from '../../services/api';
import ApiConstant from '../../constants/api.constants';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addPdfPageNumberFooter } from '../../utils/pdf-page-footer';
import './sales-production-report.css';
import orderServices from '../../services/orders/orderservice';
import RawMaterialService from '../../services/RawMaterials/RawMaterialService';

/* ------------------------------------------------------------------ */
/* Data shapes                                                        */
/* ------------------------------------------------------------------ */
interface SalesCategorySummaryDTO {
  categoryID: number;
  categoryName: string;
  measurementType: string;
  measurementCode: string;
  totalQuantity: number;
  totalPrice: number;
}

interface SalesMonthWiseSummaryDTO {
  categoryID: number;
  categoryName: string;
  saleYear: number;
  saleMonth: number;
  monthName: string;
  totalQuantity: number;
  totalPrice: number;
}

interface SalesCategoryMonthWiseSummaryDTO {
  categoryID: number;
  categoryName: string;
  subCategoryID: number;
  subCategoryName: string;
  measurementType: string;
  measurementCode: string;
  saleYear: number;
  saleMonth: number;
  monthName: string;
  totalQuantity: number;
  totalPrice: number;
}

interface SalesSubCategorySummaryDTO {
  categoryID: number;
  categoryName: string;
  subCategoryID: number;
  subCategoryName: string;
  measurementType: string;
  measurementCode: string;
  totalBaseVolume: number;
  totalPrice: number;
  [key: string]: any; // for dynamic month columns
}

interface ProcessedCategoryRow extends SalesCategorySummaryDTO {
  subCategories: SalesSubCategorySummaryDTO[];
  [key: string]: any; // for dynamic month columns
}

interface ProductionRow {
  byProductID: number;
  byProductName: string;
  posId?: number;
  posName?: string;
  totalProductionQuantity: number;
  totalProductionValue: number;
}

type ActiveTab = 'sales' | 'production';

/* ------------------------------------------------------------------ */
/* Helper – date formatter                                            */
/* ------------------------------------------------------------------ */
const aliasMU = (id: number, addr?: string, alias?: string) => {
  const code = (alias ?? "").toUpperCase().trim();
  const up = (addr ?? "").toUpperCase().replace(/\s+/g, " ");
  if (id === 3 || code === "KRN1" || /\bK\s*R\s*NAGAR\b/.test(up) || up.includes("KR NAGAR")) return "unit-1 KR";
  if (id === 4 || code === "MGT" || up.includes("MAGADI TALUK") || up.includes("MAGADI")) return "unit-2 MGT";
  return addr ?? "";
};

const formatToSQLDateTime = (date: Date, eod = false): string => {
  const d = new Date(date);
  if (eod) d.setHours(23, 59, 59, 999);
  else d.setHours(0, 0, 0, 0);
  return d.toLocaleString('sv-SE').replace(' ', 'T');
};

const fmtDate = (val: string | null) =>
  val ? new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

/* ================================================================== */
/* Main Component                                                     */
/* ================================================================== */
const SalesProductionReport: React.FC = () => {
  const toast = useRef<Toast>(null);

  /* filter state */
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [keyword, setKeyword] = useState<string>('');
  const [posList, setPosList] = useState<any[]>([]);
  const [selectedPosIds, setSelectedPosIds] = useState<number[]>([]);

  /* fetch POS list on mount */
  useEffect(() => {
    const fetchPosList = async () => {
      try {
        const list = await RawMaterialService.GetManufacrturingunitlist();
        if (list) {
          const formattedList = (list ?? []).map((unit: any) => {
            const id = Number(unit?.posID ?? unit?.PosID ?? unit?.id);
            const addr2 = unit?.address2 ?? unit?.Address2 ?? unit?.posName ?? unit?.PosName ?? "";
            const posAlias = unit?.POSAlias ?? unit?.posAlias ?? "";
            return {
              label: aliasMU(id, addr2, posAlias),
              value: id,
            };
          });
          setPosList(formattedList);
        }
      } catch (error) {
        console.error("Failed to fetch manufacturing units", error);
      }
    };
    fetchPosList();
  }, []);

  /* data state */
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [salesData, setSalesData] = useState<ProcessedCategoryRow[]>([]);
  const [salesMonths, setSalesMonths] = useState<string[]>([]);
  const [subCategoryMonths, setSubCategoryMonths] = useState<string[]>([]);
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | any[] | undefined>(undefined);
  const [productionData, setProductionData] = useState<ProductionRow[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTab>('sales');
  const [includeMonthlyWiseExport, setIncludeMonthlyWiseExport] = useState(false);


  /* ---------------------------------------------------------------- */
  /* Search handler                                                   */
  /* ---------------------------------------------------------------- */
  const handleSearch = async () => {

    if (!startDate) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please select a Start Date.', life: 3000 });
      return;
    }
    if (!endDate) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please select an End Date.', life: 3000 });
      return;
    }
    if (startDate > endDate) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Start Date must be before End Date.', life: 3000 });
      return;
    }

    setLoading(true);
    setHasSearched(true);

    const fromDateStr = formatToSQLDateTime(startDate);
    const toDateStr = formatToSQLDateTime(endDate, true);

    const prodPayload = {
      fromDate: fromDateStr,
      toDate: toDateStr,
      posId: null,
      posIds: selectedPosIds.length > 0 ? selectedPosIds : null
    };

    try {
      const salesRes = await orderServices.getSalesProductionReportSales({ fromDate: fromDateStr, toDate: toDateStr });
      const prodRes = await orderServices.getSalesProductionReportProduction(prodPayload);

      // Process Sales Data
      if (salesRes) {

        const { categorySummary, monthWiseSummary, subCategorySummary, subCategoryMonthWiseSummary } = salesRes;

        // Extract unique month names for dynamic columns (category level)
        const months = Array.from(new Set((monthWiseSummary as SalesMonthWiseSummaryDTO[]).map((m) => m.monthName)));
        setSalesMonths(months);

        // Extract unique month names for subcategory level
        const subMonths = Array.from(
          new Set((subCategoryMonthWiseSummary as SalesCategoryMonthWiseSummaryDTO[]).map((m) => m.monthName))
        );
        setSubCategoryMonths(subMonths);

        const processedSales: ProcessedCategoryRow[] = (categorySummary as SalesCategorySummaryDTO[]).map((cat) => {
          const row: ProcessedCategoryRow = { ...cat, subCategories: [] };

          // Add dynamic month price columns (category level)
          const catMonths = (monthWiseSummary as SalesMonthWiseSummaryDTO[]).filter((m) => m.categoryID === cat.categoryID);
          catMonths.forEach((m) => {
            row[`${m.monthName} TotalPrice`] = m.totalPrice;
            row[`${m.monthName} TotalQuantity`] = m.totalQuantity;
          });

          // Build subcategories with their own month-wise data
          const subCats = (subCategorySummary as SalesSubCategorySummaryDTO[]).filter((s) => s.categoryID === cat.categoryID);
          row.subCategories = subCats.map((sub) => {
            const enriched: SalesSubCategorySummaryDTO = { ...sub };
            const subMonthData = (
              subCategoryMonthWiseSummary as SalesCategoryMonthWiseSummaryDTO[]
            ).filter(
              (m) =>
                m.categoryID === cat.categoryID &&
                m.subCategoryName?.trim().toLowerCase() ===
                sub.subCategoryName?.trim().toLowerCase()
            );

            subMonthData.forEach((m) => {
              enriched[`${m.monthName} TotalPrice`] = m.totalPrice;
              enriched[`${m.monthName} TotalQuantity`] = m.totalQuantity;
            });
            return enriched;
          });

          return row;
        });

        // Optional keyword filtering
        const filteredSales = keyword
          ? processedSales.filter((r) =>
            r.categoryName.toLowerCase().includes(keyword.toLowerCase()) ||
            r.subCategories.some(sub => sub.subCategoryName.toLowerCase().includes(keyword.toLowerCase()))
          )
          : processedSales;

        setSalesData(filteredSales);
      } else {
        setSalesData([]);
        setSalesMonths([]);
      }

      setProductionData(Array.isArray(prodRes) ? prodRes : []);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load report data.', life: 4000 });
      setSalesData([]);
      setSalesMonths([]);
      setProductionData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setKeyword('');
    setSelectedPosIds([]);
    setIncludeMonthlyWiseExport(false);
    setSalesData([]);
    setSalesMonths([]);
    setSubCategoryMonths([]);
    setProductionData([]);
    setHasSearched(false);
    setActiveTab('sales');
    setExpandedRows(undefined);
  };

  /* Auto-fetch when POS units change and dates are valid */
  useEffect(() => {
    if (startDate && endDate && startDate <= endDate) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPosIds]);

  /* ---------------------------------------------------------------- */
  /* Export helpers                                                   */
  /* ---------------------------------------------------------------- */
  const saveBlob = (buffer: any, fileName: string) => {
    import('file-saver').then((mod) => {
      if (mod?.default) {
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
        });
        mod.default.saveAs(blob, `${fileName}_${Date.now()}.xlsx`);
      }
    });
  };

  const exportExcel = () => {
    if (!salesData.length && !productionData.length) {
      toast.current?.show({ severity: 'warn', summary: 'No Data', detail: 'Nothing to export.', life: 3000 });
      return;
    }

    const includeMonthlyWise = includeMonthlyWiseExport;
    const wb = XLSX.utils.book_new();

    // 1. Category Details Sheet
    if (salesData.length) {
      const categoryRows = salesData.map((r) => {
        const base: any = {
          'Category': r.categoryName,
          'Total Quantity': r.totalQuantity,
          'Total Price': r.totalPrice,
        };
        if (includeMonthlyWise) {
          salesMonths.forEach((m) => {
            base[`${m} Total Price`] = r[`${m} TotalPrice`] || 0;
            base[`${m} Total Qty`] = r[`${m} TotalQuantity`] || 0;
          });
        }
        return base;
      });
      const wsCat = XLSX.utils.json_to_sheet(categoryRows);
      XLSX.utils.book_append_sheet(wb, wsCat, 'Category Details');

      // 2. Subcategories Details Sheet
      const subCategoryRows: any[] = [];
      salesData.forEach((cat) => {
        cat.subCategories.forEach((sub: SalesSubCategorySummaryDTO) => {
          const base: any = {
            'Category': cat.categoryName,
            'Product': sub.subCategoryName,
            'Total Quantity': sub.totalBaseVolume,
            'UOM': sub.measurementCode,
            'Total Price': sub.totalPrice,
          };
          if (includeMonthlyWise) {
            subCategoryMonths.forEach((m) => {
              base[`${m} Total Price`] = sub[`${m} TotalPrice`] || 0;
              base[`${m} Total Qty`] = sub[`${m} TotalQuantity`] || 0;
            });
          }
          subCategoryRows.push(base);
        });
      });
      const wsSub = XLSX.utils.json_to_sheet(subCategoryRows);
      XLSX.utils.book_append_sheet(wb, wsSub, 'Product Details');
    }

    // 3. Production Details Sheet
    if (productionData && productionData.length) {
      let prodRows: any[] = [];
      if (selectedPosIds.length <= 1) {
        // None or single selected
        const grouped = getProcessedProductionData();
        prodRows = grouped.map((r) => ({
          'Product': r.byProductName,
          'Quantity': r.totalProductionQuantity,
          'Value': r.totalProductionValue,
        }));
      } else {
        // Multiple selected (e.g. both)
        const unitLabelsMap: { [key: number]: string } = {};
        selectedPosIds.forEach(id => {
          unitLabelsMap[id] = posList.find(p => p.value === id)?.label || `Unit-${id}`;
        });

        // Group data by product and map quantities for each selected unit
        const groupedMap: { [key: string]: any } = {};
        productionData.forEach((row) => {
          const productName = row.byProductName ?? (row as any).ByProductName;
          const posId = row.posId ?? (row as any).PosId ?? (row as any).posID ?? (row as any).PosID;
          const qty = row.totalProductionQuantity ?? (row as any).TotalProductionQuantity ?? 0;
          const val = row.totalProductionValue ?? (row as any).TotalProductionValue ?? 0;

          if (!productName) return;

          const key = productName;
          if (!groupedMap[key]) {
            groupedMap[key] = {
              productName: key,
              quantities: {},
              totalValue: 0
            };
            selectedPosIds.forEach(id => {
              groupedMap[key].quantities[id] = 0;
            });
          }
          if (posId && groupedMap[key].quantities[posId] !== undefined) {
            groupedMap[key].quantities[posId] += qty;
          }
          groupedMap[key].totalValue += val;
        });

        prodRows = Object.values(groupedMap).map((item: any) => {
          const rowObj: any = {
            'Product': item.productName
          };
          let totalQty = 0;
          selectedPosIds.forEach(id => {
            const label = unitLabelsMap[id];
            const qty = Number(item.quantities[id].toFixed(2));
            rowObj[label] = qty;
            totalQty += qty;
          });
          rowObj['Total Quantity'] = Number(totalQty.toFixed(2));
          rowObj['Value'] = Number(item.totalValue.toFixed(2));
          return rowObj;
        });
      }
      const wsProd = XLSX.utils.json_to_sheet(prodRows);
      XLSX.utils.book_append_sheet(wb, wsProd, 'Production Details');
    } else {
      // Append empty sheet if no data so the structure is consistent
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{ Message: "No production data found" }]), 'Production Details');
    }

    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveBlob(buffer, 'Sales_And_Production_Report');
  };

  const exportPDF = () => {
    if (!salesData.length && !productionData.length) {
      toast.current?.show({ severity: 'warn', summary: 'No Data', detail: 'Nothing to export.', life: 3000 });
      return;
    }

    const includeMonthlyWise = includeMonthlyWiseExport;
    // Dynamically calculate page width based on column count to avoid shrinking text
    const maxMonthsCount = includeMonthlyWise ? Math.max(salesMonths.length, subCategoryMonths.length) : 0;
    const totalColumns = 4 + (maxMonthsCount * 2); // Base cols + (Months * Price/Qty)
    const pageWidth = Math.max(297, totalColumns * 22); // A4 Landscape is 297mm. Assign ~22mm minimum per column
    const pageHeight = 297; // Keeps sufficient vertical room

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [pageWidth, pageHeight] });

    doc.setFontSize(16);
    doc.text('Sales and Production Report', 14, 18);
    doc.setFontSize(11);
    doc.text(
      `Period: ${startDate ? fmtDate(startDate.toISOString()) : ''} – ${endDate ? fmtDate(endDate.toISOString()) : ''}`,
      14,
      26,
    );

    let currentY = 36;

    // 1. Category Details
    if (salesData.length) {
      doc.setFontSize(13);
      doc.text('Category Details', 14, currentY);
      currentY += 6;

      const catHead: any[] = includeMonthlyWise
        ? [
          [
            { content: 'Category', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fillColor: [76, 175, 80], textColor: 255 } },
            { content: 'Total Qty', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fillColor: [76, 175, 80], textColor: 255 } },
            { content: 'Total Price', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fillColor: [76, 175, 80], textColor: 255 } },
            ...salesMonths.map((m) => ({
              content: m,
              colSpan: 2,
              styles: { halign: 'center', fillColor: [76, 175, 80], textColor: 255 },
            })),
          ],
          [
            ...salesMonths.flatMap(() => [
              { content: 'Price', styles: { halign: 'center', fillColor: [200, 230, 201], textColor: [27, 94, 32] } },
              { content: 'Qty', styles: { halign: 'center', fillColor: [232, 245, 233], textColor: [46, 125, 50] } },
            ]),
          ],
        ]
        : [['Category', 'Total Qty', 'Total Price']];

      const catBody = salesData.map((r) => (
        includeMonthlyWise
          ? [
            r.categoryName, r.totalQuantity, r.totalPrice,
            ...salesMonths.flatMap((m) => [r[`${m} TotalPrice`] || 0, r[`${m} TotalQuantity`] || 0])
          ]
          : [r.categoryName, r.totalQuantity, r.totalPrice]
      ));

      autoTable(doc, {
        startY: currentY,
        head: catHead,
        body: catBody,
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [76, 175, 80] },
        theme: 'grid',
      });
      currentY = (doc as any).lastAutoTable.finalY + 15;

      // 2. Subcategory Details
      doc.setFontSize(13);
      doc.text('Product Details', 14, currentY);
      currentY += 6;

      const subHead: any[] = includeMonthlyWise
        ? [
          [
            { content: 'Category', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fillColor: [76, 175, 80], textColor: 255 } },
            { content: 'Product', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fillColor: [76, 175, 80], textColor: 255 } },
            { content: 'Total Qty', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fillColor: [76, 175, 80], textColor: 255 } },
            { content: 'UOM', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fillColor: [76, 175, 80], textColor: 255 } },
            { content: 'Total Price', rowSpan: 2, styles: { halign: 'center', valign: 'middle', fillColor: [76, 175, 80], textColor: 255 } },
            ...subCategoryMonths.map((m) => ({
              content: m,
              colSpan: 2,
              styles: { halign: 'center', fillColor: [76, 175, 80], textColor: 255 },
            })),
          ],
          [
            ...subCategoryMonths.flatMap(() => [
              { content: 'Price', styles: { halign: 'center', fillColor: [200, 230, 201], textColor: [27, 94, 32] } },
              { content: 'Qty', styles: { halign: 'center', fillColor: [232, 245, 233], textColor: [46, 125, 50] } },
            ]),
          ],
        ]
        : [['Category', 'Product', 'Total Qty', 'UOM', 'Total Price']];

      const subBody: any[] = [];
      salesData.forEach(cat => {
        cat.subCategories.forEach(sub => {
          subBody.push(
            includeMonthlyWise
              ? [
                cat.categoryName, sub.subCategoryName, sub.totalBaseVolume, sub.measurementCode, sub.totalPrice,
                ...subCategoryMonths.flatMap((m) => [sub[`${m} TotalPrice`] || 0, sub[`${m} TotalQuantity`] || 0])
              ]
              : [cat.categoryName, sub.subCategoryName, sub.totalBaseVolume, sub.measurementCode, sub.totalPrice]
          );
        });
      });

      autoTable(doc, {
        startY: currentY,
        head: subHead,
        body: subBody,
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [76, 175, 80] },
        theme: 'grid',
      });
      currentY = (doc as any).lastAutoTable.finalY + 15;
    }

    // 3. Production Details
    doc.setFontSize(13);
    doc.text('Production Details', 14, currentY);
    currentY += 6;

    if (productionData && productionData.length) {
      let pdfHead: string[][];
      let pdfBody: any[][];

      if (selectedPosIds.length <= 1) {
        // None or single selected
        pdfHead = [['Product', 'Quantity', 'Value']];
        const grouped = getProcessedProductionData();
        pdfBody = grouped.map((r) => [
          r.byProductName,
          r.totalProductionQuantity,
          r.totalProductionValue
        ]);
      } else {
        // Multiple selected (e.g. both)
        const unitLabels = selectedPosIds.map(id => posList.find(p => p.value === id)?.label || `Unit-${id}`);
        pdfHead = [['Product', ...unitLabels, 'Total Quantity', 'Value']];

        // Group data by product and map quantities for each selected unit
        const groupedMap: { [key: string]: any } = {};
        productionData.forEach((row) => {
          const productName = row.byProductName ?? (row as any).ByProductName;
          const posId = row.posId ?? (row as any).PosId ?? (row as any).posID ?? (row as any).PosID;
          const qty = row.totalProductionQuantity ?? (row as any).TotalProductionQuantity ?? 0;
          const val = row.totalProductionValue ?? (row as any).TotalProductionValue ?? 0;

          if (!productName) return;

          const key = productName;
          if (!groupedMap[key]) {
            groupedMap[key] = {
              productName: key,
              quantities: {},
              totalValue: 0
            };
            selectedPosIds.forEach(id => {
              groupedMap[key].quantities[id] = 0;
            });
          }
          if (posId && groupedMap[key].quantities[posId] !== undefined) {
            groupedMap[key].quantities[posId] += qty;
          }
          groupedMap[key].totalValue += val;
        });

        pdfBody = Object.values(groupedMap).map((item: any) => {
          const rowData = [item.productName];
          let totalQty = 0;
          selectedPosIds.forEach(id => {
            const qty = Number(item.quantities[id].toFixed(2));
            rowData.push(qty);
            totalQty += qty;
          });
          rowData.push(Number(totalQty.toFixed(2)));
          rowData.push(Number(item.totalValue.toFixed(2)));
          return rowData;
        });
      }

      autoTable(doc, {
        startY: currentY,
        head: pdfHead,
        body: pdfBody,
        styles: { fontSize: 9, cellPadding: 4 },
        headStyles: { fillColor: [76, 175, 80] },
        theme: 'grid'
      });
    } else {
      doc.setFontSize(10);
      doc.text('No production data found for this period.', 14, currentY + 5);
    }

    addPdfPageNumberFooter(doc);
    doc.save(`Sales And Production Report ${Date.now()}.pdf`);
  };

  /* ---------------------------------------------------------------- */
  /* Body templates                                                   */
  /* ---------------------------------------------------------------- */
  const currencyBody = (val: number) => (
    <span style={{ fontWeight: 600 }}>₹{(val ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
  );

  /* Month column highlight styles – project green theme */

  // Parent month group header: dark green (matches section icons & filter button)
  const monthGroupHeaderStyle: React.CSSProperties = {
    background: 'rgb(76,175,80)',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '0.88rem',
    textAlign: 'center',
    borderBottom: '2px solid #4caf50',
    letterSpacing: '0.05em',
  };

  // Total Price sub-column: medium green tint
  const monthPriceHeaderStyle: React.CSSProperties = {
    minWidth: '130px',
    background: 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)',
    color: '#1b5e20',
    fontWeight: 700,
    borderLeft: '2px solid #4caf50',
    textAlign: 'center',
  };
  const monthPriceCellStyle: React.CSSProperties = {
    background: '#f1f8f1',
    borderLeft: '2px solid #c8e6c9',
  };

  // Total Qty sub-column: lightest green tint
  const monthQtyHeaderStyle: React.CSSProperties = {
    minWidth: '130px',
    background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
    color: '#2e7d32',
    fontWeight: 700,
    borderLeft: '2px solid #81c784',
    textAlign: 'center',
  };
  const monthQtyCellStyle: React.CSSProperties = {
    background: '#f9fdf9',
    borderLeft: '2px solid #e8f5e9',
  };

  const monthPriceBody = (val: number | null | undefined) => (
    <span style={{ fontWeight: 700, color: '#1b5e20' }}>
      ₹{(val ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
    </span>
  );

  const monthQtyBody = (val: number | null | undefined) => (
    <span style={{ fontWeight: 700, color: '#2e7d32' }}>{val ?? 0}</span>
  );

  /* ---------------------------------------------------------------- */
  /* Column Group Headers (grouped month columns)                     */
  /* ---------------------------------------------------------------- */
  const salesHeaderGroup = (
    <ColumnGroup>
      {/* Row 1 – static headers span 2 rows; each month spans 2 cols */}
      <Row>
        <Column header="" rowSpan={2} style={{ width: '5rem' }} />
        <Column header="Category" field="categoryName" sortable rowSpan={2} style={{ minWidth: '150px', fontWeight: 700 }} />
        <Column header="Total Quantity" field="totalQuantity" sortable rowSpan={2} style={{ minWidth: '120px', fontWeight: 700 }} />
        <Column header="Total Price" field="totalPrice" sortable rowSpan={2} style={{ minWidth: '120px', fontWeight: 700 }} />
        {salesMonths.map((month) => (
          <Column key={`grp-${month}`} header={month} colSpan={2} headerStyle={{ ...monthGroupHeaderStyle, textAlign: 'center' }} />
        ))}
      </Row>
      {/* Row 2 – sub-headers: Total Price | Total Quantity per month */}
      <Row>
        {salesMonths.flatMap((month) => [
          <Column key={`grp-qty-${month}`} field={`${month} TotalQuantity`} sortable header="Total Qty" headerStyle={monthQtyHeaderStyle} />,
          <Column key={`grp-price-${month}`} field={`${month} TotalPrice`} sortable header="Total Price" headerStyle={monthPriceHeaderStyle} />,
        ])}
      </Row>
    </ColumnGroup>
  );

  /* ---------------------------------------------------------------- */
  /* Row Expansion Template for Sales Subcategories                     */
  /* ---------------------------------------------------------------- */
  const rowExpansionTemplate = (data: ProcessedCategoryRow) => {
    const subHeaderGroup = (
      <ColumnGroup>
        <Row>
          <Column header="Subcategory Name" field="subCategoryName" sortable rowSpan={2} style={{ minWidth: '150px', fontWeight: 700 }} />
          <Column header="Total Quantity" field="totalBaseVolume" sortable rowSpan={2} style={{ minWidth: '130px', fontWeight: 700 }} />
          <Column header="UOM" field="measurementCode" sortable rowSpan={2} style={{ minWidth: '120px', fontWeight: 700 }} />
          <Column header="Total Price" field="totalPrice" sortable rowSpan={2} style={{ minWidth: '120px', fontWeight: 700 }} />
          {subCategoryMonths.map((month) => (
            <Column key={`sub-grp-${month}`} header={month} colSpan={2} headerStyle={{ ...monthGroupHeaderStyle, textAlign: 'center' }} />
          ))}
        </Row>
        <Row>
          {subCategoryMonths.flatMap((month) => [
            <Column key={`sub-grp-qty-${month}`} field={`${month} TotalQuantity`} sortable header="Total Qty" headerStyle={monthQtyHeaderStyle} />,
            <Column key={`sub-grp-price-${month}`} field={`${month} TotalPrice`} sortable header="Total Price" headerStyle={monthPriceHeaderStyle} />,
          ])}
        </Row>
      </ColumnGroup>
    );

    return (
      <div className="p-3" style={{ backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px dashed #ced4da', margin: '0.5rem 0' }}>
        <h6 className="mb-2" style={{ color: '#4b5563', fontWeight: 600 }}>Subcategories for {data.categoryName}</h6>
        <DataTable
          value={data.subCategories}
          size="small"
          emptyMessage="No subcategories found."
          scrollable
          headerColumnGroup={subHeaderGroup}
          style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0' }}
        >
          <Column field="subCategoryName" sortable style={{ minWidth: '150px' }} />
          <Column field="totalBaseVolume" sortable style={{ minWidth: '130px' }} />
          <Column field="measurementCode" sortable style={{ minWidth: '120px' }} />
          <Column
            field="totalPrice"
            sortable
            style={{ minWidth: '120px' }}
            body={(r: SalesSubCategorySummaryDTO) => currencyBody(r.totalPrice)}
          />
          {subCategoryMonths.flatMap((month) => [
            <Column
              key={`sub-qty-${month}`}
              field={`${month} TotalQuantity`}
              sortable
              bodyStyle={monthQtyCellStyle}
              body={(r: SalesSubCategorySummaryDTO) => monthQtyBody(r[`${month} TotalQuantity`])}
            />,
            <Column
              key={`sub-price-${month}`}
              field={`${month} TotalPrice`}
              sortable
              bodyStyle={monthPriceCellStyle}
              body={(r: SalesSubCategorySummaryDTO) => monthPriceBody(r[`${month} TotalPrice`])}
            />,
          ])}
        </DataTable>
      </div>
    );
  };


  const getProcessedProductionData = () => {
    const groupedMap: { [key: string]: any } = {};
    productionData.forEach((row) => {
      const productName = row.byProductName ?? (row as any).ByProductName;
      const posId = row.posId ?? (row as any).PosId ?? (row as any).posID ?? (row as any).PosID;
      const qty = row.totalProductionQuantity ?? (row as any).TotalProductionQuantity ?? 0;
      const val = row.totalProductionValue ?? (row as any).TotalProductionValue ?? 0;
      const productId = row.byProductID ?? (row as any).ByProductID ?? (row as any).byProductId ?? (row as any).ByProductId;

      if (!productName) return;

      const key = productName;
      if (!groupedMap[key]) {
        groupedMap[key] = {
          byProductID: productId,
          byProductName: productName,
          totalProductionQuantity: 0,
          totalProductionValue: 0,
        };
        // Initialize dynamic fields for all selected POS IDs
        selectedPosIds.forEach(id => {
          groupedMap[key][`quantity_${id}`] = 0;
        });
      }
      if (posId && groupedMap[key][`quantity_${posId}`] !== undefined) {
        groupedMap[key][`quantity_${posId}`] += qty;
      }
      groupedMap[key].totalProductionQuantity += qty;
      groupedMap[key].totalProductionValue += val;
    });

    return Object.values(groupedMap).map((row: any) => {
      const formatted: any = {
        byProductID: row.byProductID,
        byProductName: row.byProductName,
        totalProductionQuantity: Number(row.totalProductionQuantity.toFixed(2)),
        totalProductionValue: Number(row.totalProductionValue.toFixed(2)),
      };
      selectedPosIds.forEach(id => {
        formatted[`quantity_${id}`] = Number(row[`quantity_${id}`].toFixed(2));
      });
      return formatted;
    });
  };

  const processedProductionData = getProcessedProductionData();

  const activeCount = activeTab === 'sales' ? salesData.length : processedProductionData.length;

  /* ---------------------------------------------------------------- */
  /* Render                                                           */
  /* ---------------------------------------------------------------- */
  return (
    <Suspense fallback={<AppLoading />}>
      <Toast ref={toast} position="top-right" />

      <div className="layout-content mt-3 mb-3">
        <div className="card w-full p-4">

          {/* ── Header Card ── */}
          <div className="card p-4 w-full mb-4 spr-header-card">
            <div className="spr-header-gradient">
              <div className="spr-header-icon">
                <i className="pi pi-chart-line" />
              </div>
              <div className="spr-header-text">
                <h5>Sales &amp; Production Summary</h5>
                <p>View and export sales and production reports for a selected date range</p>
              </div>
            </div>

            {/* ── Filter Row ── */}
            <div className="spr-filter-label mb-3">
              <i className="pi pi-filter" style={{ color: 'rgb(76,175,80)', fontSize: '1.05rem' }} />
              <span>Filter Report</span>
            </div>

            <div className="grid spr-filter-grid">
              {/* Start Date */}
              <div className={activeTab === 'production' ? "col-12 md:col-2" : "col-12 md:col-3"}>
                <label className="spr-filter-label-text">Start Date</label>
                <Calendar
                  id="spr-start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.value as Date | null)}
                  showButtonBar
                  dateFormat="dd/mm/yy"
                  placeholder="Start Date"
                  className="w-full"
                  readOnlyInput
                  inputStyle={{ borderRadius: '8px' }}
                />
              </div>

              {/* End Date */}
              <div className={activeTab === 'production' ? "col-12 md:col-2" : "col-12 md:col-3"}>
                <label className="spr-filter-label-text">End Date</label>
                <Calendar
                  id="spr-end-date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.value as Date | null)}
                  showButtonBar
                  dateFormat="dd/mm/yy"
                  placeholder="End Date"
                  className="w-full"
                  readOnlyInput
                  inputStyle={{ borderRadius: '8px' }}
                />
              </div>

              {/* POS Unit - visible only in production tab */}
              {activeTab === 'production' && (
                <div className="col-12 md:col-2">
                  <label className="spr-filter-label-text">MFG Unit</label>
                  <MultiSelect
                    id="spr-pos"
                    value={selectedPosIds}
                    options={posList}
                    onChange={(e) => setSelectedPosIds(e.value)}
                    placeholder="Select POS"
                    display="chip"
                    className="w-full"
                    style={{ borderRadius: '8px' }}
                  />
                </div>
              )}

              {/* Keyword Search */}
              <div className="col-12 md:col-3">
                <label className="spr-filter-label-text">Search</label>
                <span className="p-input-icon-left w-full">
                  <i className="pi pi-search" />
                  <InputText
                    id="spr-keyword"
                    type="search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="Search by name, product…"
                    className="w-full"
                    style={{ borderRadius: '8px' }}
                  />
                </span>
              </div>

              {/* Action Buttons */}
              <div className="col-12 md:col-3 flex align-items-end gap-2">
                <Button
                  id="spr-filter-btn"
                  label="Filter"
                  icon="pi pi-search"
                  severity="success"
                  className="w-full"
                  style={{ borderRadius: '8px', background: '#0e6f3b', border: 'none' }}
                  onClick={handleSearch}
                  loading={loading}
                />
                <Button
                  id="spr-reset-btn"
                  label="Reset"
                  icon="pi pi-refresh"
                  severity="secondary"
                  className="w-full"
                  text
                  raised
                  style={{ borderRadius: '8px' }}
                  onClick={handleReset}
                />
              </div>
            </div>
          </div>

          {/* ── Always-visible Tab Switcher ── */}
          <div className="flex align-items-center gap-2 mb-3 flex-wrap">
            <Button
              id="spr-tab-sales"
              label="Sales"
              icon="pi pi-shopping-cart"
              className="spr-tab-btn"
              style={{
                backgroundColor: activeTab === 'sales' ? 'rgb(76,175,80)' : 'transparent',
                color: activeTab === 'sales' ? 'white' : 'rgb(76,175,80)',
                border: '1px solid rgb(76,175,80)',
                borderRadius: '6px',
                padding: '0.5rem 1.25rem',
                transition: 'all 0.3s ease',
                fontWeight: activeTab === 'sales' ? 600 : 500,
              }}
              onClick={() => setActiveTab('sales')}
            />
            <Button
              id="spr-tab-production"
              label="Production"
              icon="pi pi-cog"
              className="spr-tab-btn"
              style={{
                backgroundColor: activeTab === 'production' ? 'rgb(76,175,80)' : 'transparent',
                color: activeTab === 'production' ? 'white' : 'rgb(76,175,80)',
                border: '1px solid rgb(76,175,80)',
                borderRadius: '6px',
                padding: '0.5rem 1.25rem',
                transition: 'all 0.3s ease',
                fontWeight: activeTab === 'production' ? 600 : 500,
              }}
              onClick={() => setActiveTab('production')}
            />

            {/* Record count + export buttons – shown after search with results */}
            {hasSearched && activeCount > 0 && (
              <>
                <span className="spr-record-count align-self-center">
                  {activeCount} record{activeCount !== 1 ? 's' : ''}
                </span>
                <div className="spr-export-actions ml-auto">
                  <div className="spr-export-option">
                    <Checkbox
                      inputId="spr-monthly-wise-export"
                      checked={includeMonthlyWiseExport}
                      onChange={(e) => setIncludeMonthlyWiseExport(Boolean(e.checked))}
                    />
                    <label htmlFor="spr-monthly-wise-export" className="spr-export-option-label">
                      Monthly wise
                    </label>
                  </div>
                  <div className="spr-export-btns">
                    <FileActionButton action="export"
                      id="spr-export-excel-btn"
                      icon="pi pi-file-excel"
                      label="Excel"
                      className="p-button-sm spr-btn-excel"
                      onClick={exportExcel}
                      tooltip={`Export ${activeCount} record(s) to Excel${includeMonthlyWiseExport ? ' with monthly-wise columns' : ''}`}
                      tooltipOptions={{ position: 'top' }}
                    />
                    <FileActionButton action="export"
                      id="spr-export-pdf-btn"
                      icon="pi pi-file-pdf"
                      label="PDF"
                      className="p-button-sm spr-btn-pdf"
                      onClick={exportPDF}
                      tooltip={`Export ${activeCount} record(s) to PDF${includeMonthlyWiseExport ? ' with monthly-wise columns' : ''}`}
                      tooltipOptions={{ position: 'top' }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ── Sales Tab Content ── */}
          {activeTab === 'sales' && (
            <div className="spr-table-section">
              <div className="spr-table-header mb-2">
                <div className="spr-section-icon" style={{ backgroundColor: 'rgb(76,175,80)' }}>
                  <i className="pi pi-shopping-cart" style={{ color: 'white', fontSize: '1.1rem' }} />
                </div>
                <span className="spr-section-title">Sales Records (Category Overview)</span>
              </div>

              {hasSearched ? (
                <div className="card p-2" style={{ borderRadius: '12px' }}>
                  <DataTable
                    value={salesData}
                    loading={loading}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    stripedRows
                    rowHover
                    sortMode="multiple"
                    expandedRows={expandedRows}
                    onRowToggle={(e) => setExpandedRows(e.data)}
                    rowExpansionTemplate={rowExpansionTemplate}
                    dataKey="categoryID"
                    emptyMessage="No sales category records found for the selected period."
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    className="myorders-datatable"
                    scrollable
                    headerColumnGroup={salesHeaderGroup}
                    style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0' }}
                  >
                    <Column expander style={{ width: '5rem' }} />
                    <Column field="categoryName" sortable style={{ minWidth: '150px' }} />
                    <Column field="totalQuantity" sortable style={{ minWidth: '120px' }} />
                    <Column field="totalPrice" sortable style={{ minWidth: '120px' }} body={(r: ProcessedCategoryRow) => currencyBody(r.totalPrice)} />

                    {/* Dynamic month Columns – header is handled by salesHeaderGroup ColumnGroup */}
                    {salesMonths.flatMap((month) => [
                      <Column
                        key={`cat-qty-${month}`}
                        field={`${month} TotalQuantity`}
                        sortable
                        bodyStyle={monthQtyCellStyle}
                        body={(r: ProcessedCategoryRow) => monthQtyBody(r[`${month} TotalQuantity`])}
                      />,
                      <Column
                        key={`cat-price-${month}`}
                        field={`${month} TotalPrice`}
                        sortable
                        bodyStyle={monthPriceCellStyle}
                        body={(r: ProcessedCategoryRow) => monthPriceBody(r[`${month} TotalPrice`])}
                      />,
                    ])}
                  </DataTable>
                </div>
              ) : (
                <div className="spr-placeholder">
                  <i className="pi pi-shopping-cart spr-placeholder-icon" />
                  <p className="spr-placeholder-text">
                    Select a <strong>Start Date</strong> and <strong>End Date</strong>, then click <strong>Filter</strong> to load the Sales report.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Production Tab Content ── */}
          {activeTab === 'production' && (
            <div className="spr-table-section">
              <div className="spr-table-header mb-2">
                <div className="spr-section-icon" style={{ backgroundColor: 'rgb(76,175,80)' }}>
                  <i className="pi pi-cog" style={{ color: 'white', fontSize: '1.1rem' }} />
                </div>
                <span className="spr-section-title">Production Records</span>
              </div>

              {hasSearched ? (
                <div className="card p-2" style={{ borderRadius: '12px' }}>
                  <DataTable
                    value={processedProductionData}
                    loading={loading}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    stripedRows
                    rowHover
                    sortMode="multiple"
                    emptyMessage="No production records found for the selected period."
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    className="myorders-datatable"
                    scrollable
                    style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0' }}
                  >
                    <Column field="byProductName" header="Product" sortable style={{ minWidth: '140px' }} />
                    {selectedPosIds.length <= 1 && (
                      <Column field="totalProductionQuantity" header="Quantity" sortable style={{ minWidth: '160px' }} />
                    )}
                    {selectedPosIds.length > 1 && selectedPosIds.map(id => {
                      const label = posList.find(p => p.value === id)?.label || `Unit-${id}`;
                      return (
                        <Column
                          key={`col-unit-${id}`}
                          field={`quantity_${id}`}
                          header={label}
                          sortable
                          style={{ minWidth: '130px' }}
                        />
                      );
                    })}
                    {selectedPosIds.length > 1 && (
                      <Column field="totalProductionQuantity" header="Total Quantity" sortable style={{ minWidth: '160px' }} />
                    )}
                    <Column field="totalProductionValue" header="Value" sortable style={{ minWidth: '160px' }} />
                  </DataTable>
                </div>
              ) : (
                <div className="spr-placeholder">
                  <i className="pi pi-cog spr-placeholder-icon" />
                  <p className="spr-placeholder-text">
                    Select a <strong>Start Date</strong> and <strong>End Date</strong>, then click <strong>Filter</strong> to load the Production report.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {loading && <AppLoading />}
    </Suspense>
  );
};

export default SalesProductionReport;
